// The context card an integration pinned to the call (API-08, BOX-10), shown inside the call card below the call
// controls so a card arriving mid-call never moves them. Card content is untrusted: text nodes only, https links
// that always show their host, and an optional pop-out window for the first link. Rules: contextCard.ts.
import type { Call, CallContextCard, CallDispositionReceipt } from '@bcio-voice/contracts';
import { type RefObject, useContext, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { AppWindow, IdCard } from 'lucide-react';
import { Icon } from '../../ui/Icon.tsx';
import { useAnnouncer } from '../../ui/Announcer.tsx';
import { contextCardOf, isLive } from '../../call/callStore.ts';
import type { DataEnvelope } from '../../lib/api.ts';
import { capability } from '../../lib/capabilities.ts';
import { formatAbsolute, formatRelative } from '../../lib/format.ts';
import { WorkspaceContext } from '../../state/workspace.tsx';
import {
  type ContextCardModel, type PreviousCardResult, AUTO_POP_DEFAULT, autoPopDecision, autoPopPreference, awaitedCardRemainingMs, awaitedCardState,
  awaitedCardText, blockedText, browserOpener, cardExpected, carriedCardText, contextCardModel, dispositionRequest, isCollapsible,
  popOut, previousCardPath, previousCards, refusedLinksText, seenCards, shouldAutoFocusPop, shownFields, toggleText,
} from './contextCard.ts';

/** The call's own card when it has one. Before it arrives, a live call with an outside party in a workspace with CRM call
 * webhooks shows the card from the last call with the same number, or a short looking / not-here-yet line. Otherwise nothing. */
export function ContextCard({ call, headingLevel = 3 }: { call: Call; headingLevel?: 3 | 4 }) {
  const ws = useContext(WorkspaceContext);
  const card = contextCardOf(call);
  if (card) return <PinnedContextCard call={call} card={card} headingLevel={headingLevel} />;
  if (!cardExpected(call, capability(ws?.config.value ?? null, 'crm.sync'))) return null;
  return <AwaitedContextCard key={call.id} call={call} headingLevel={headingLevel} />;
}

const RELATIVE_TICK_MS = 30_000;

/**
 * The "CRM page" toggle for live calls whose card no longer renders in the dock (the call window shows it): the first
 * time a call's card version arrives with an https link while the call is live, the CRM's own page opens in
 * 'sidetone-pipeline', exactly as PinnedContextCard does at ring and wrap-up. The shared seenCards keeps it to once per
 * card version across both. It runs outside a gesture, so the browser blocks it until pop-ups are allowed.
 */
export function useCrmPageAutoPop(calls: readonly Call[]): void {
  const signature = calls.map(c => `${c.id}:${c.state}:${contextCardOf(c)?.version ?? 0}`).join('|');
  const latest = useRef(calls);
  latest.current = calls;
  useEffect(() => {
    for (const call of latest.current) {
      const card = contextCardOf(call);
      const decision = autoPopDecision({ seen: seenCards, callId: call.id, card, callState: call.state, enabled: autoPopPreference.get() });
      if (!decision.key || !decision.fresh || !card) continue;
      seenCards.add(decision.key);
      const primary = contextCardModel(card).links[0];
      if (decision.pop && primary) popOut(primary.href, browserOpener());
    }
  }, [signature]);
}

/** Never blocks the phone: it only renders what has arrived and asks once for the previous card. */
function AwaitedContextCard({ call, headingLevel }: { call: Call; headingLevel: 3 | 4 }) {
  const ws = useContext(WorkspaceContext);
  const [now, setNow] = useState(() => Date.now());
  const [previous, setPrevious] = useState<PreviousCardResult | null>(() => previousCards.peek(call.id));
  const state = awaitedCardState(call, now);

  useEffect(() => {
    if (state !== 'loading') return;
    const t = setTimeout(() => setNow(Date.now()), awaitedCardRemainingMs(call, Date.now()) + 50);
    return () => clearTimeout(t);
  }, [state, call]);

  useEffect(() => {
    const api = ws?.api;
    if (!api) return;
    let current = true;
    void previousCards.load(call.id, () => api.request<unknown>(previousCardPath(call.id))).then(result => { if (current) setPrevious(result); });
    return () => { current = false; };
  }, [ws?.api, call.id]);

  return <AwaitedContextCardView callId={call.id} headingLevel={headingLevel} state={state} previous={previous} now={now} />;
}

export interface AwaitedContextCardViewProps {
  callId: string;
  headingLevel?: 3 | 4;
  state: ReturnType<typeof awaitedCardState>;
  previous: PreviousCardResult | null;
  now: number;
}

/** Presentational awaited card (exported for render tests): the previous card with a status line, or the status line alone. */
export function AwaitedContextCardView({ callId, headingLevel = 3, state, previous, now }: AwaitedContextCardViewProps) {
  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  const id = `context-${callId}`;
  const status = awaitedCardText(state, !!previous?.card);
  if (previous?.card) {
    const model = contextCardModel(previous.card);
    return (
      <ContextCardView
        callId={callId}
        model={model}
        headingLevel={headingLevel}
        expanded
        onToggle={() => undefined}
        autoPop={false}
        onAutoPop={() => undefined}
        onPop={() => { const primary = model.links[0]; if (primary) popOut(primary.href, browserOpener(), true); }}
        blocked={false}
        pageHost={null}
        now={now}
        carried={{ note: carriedCardText(previous.fromCallAt, now, formatRelative), status }}
      />
    );
  }
  return (
    <section className="capture-band ctx-card ctx-awaited" aria-labelledby={`${id}-title`} aria-busy={state === 'loading'} data-card-state={state}>
      <ContextCardStyles />
      <Heading id={`${id}-title`} className="capture-title">
        <Icon icon={IdCard} />
        <span className="ctx-title-text">CRM card</span>
      </Heading>
      <p className="meta" role="status">{status}</p>
    </section>
  );
}

function PinnedContextCard({ call, card, headingLevel }: { call: Call; card: CallContextCard; headingLevel: 3 | 4 }) {
  const ws = useContext(WorkspaceContext);
  const { announce } = useAnnouncer();
  const model = useMemo(() => contextCardModel(card), [card]);
  // Candidate screen pops open automatically; generic cards keep their collapse state.
  const [expanded, setExpanded] = useState(() => model.kind === 'candidate');
  const [blockedVersion, setBlockedVersion] = useState<number | null>(null);
  const [selectedDisposition, setSelectedDisposition] = useState<string | null>(null);
  const [dispositionPending, setDispositionPending] = useState(false);
  const [dispositionStatus, setDispositionStatus] = useState<string | null>(null);
  const autoPop = useSyncExternalStore(autoPopPreference.subscribe, autoPopPreference.get, () => AUTO_POP_DEFAULT);
  const [now, setNow] = useState(() => Date.now());
  const focusRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), RELATIVE_TICK_MS);
    return () => clearInterval(t);
  }, []);

  // Each card version is handled once per tab: announced while the call is live, focused when a candidate
  // screen pop arrives on ring or dial, and popped out when this browser opted in.
  const shownHere = useRef(false);
  useEffect(() => {
    const focusDecision = shouldAutoFocusPop({ seen: seenCards, callId: call.id, card, callState: call.state });
    const decision = autoPopDecision({ seen: seenCards, callId: call.id, card, callState: call.state, enabled: autoPopPreference.get() });
    if (!decision.key || !decision.fresh) return;
    seenCards.add(decision.key);
    const view = contextCardModel(card);
    if (view.kind === 'candidate') setExpanded(true);
    if (focusDecision.pop) focusRef.current?.focus();
    // Words only: the card's own text stays on screen, never in the live region.
    if (isLive(call)) {
      announce(shownHere.current ? `Context card updated by ${view.source}` : `Context card from ${view.source}`);
    }
    shownHere.current = true;
    const primary = view.links[0];
    if (decision.pop && primary) setBlockedVersion(popOut(primary.href, browserOpener()) === 'blocked' ? card.version : null);
  }, [call, card, announce]);

  const popPrimary = () => {
    const primary = model.links[0];
    if (!primary) return;
    setBlockedVersion(popOut(primary.href, browserOpener(), true) === 'blocked' ? card.version : null);
  };

  const recordDisposition = (dispositionId: string) => {
    const spec = dispositionRequest(call.id, dispositionId, model.candidateId);
    setSelectedDisposition(spec.body.disposition);
    if (!ws?.api) {
      setDispositionStatus('Saved to CRM ledger');
      return;
    }
    setDispositionPending(true);
    setDispositionStatus(null);
    void ws.api.request<DataEnvelope<CallDispositionReceipt>>(spec.path, { method: spec.method, body: JSON.stringify(spec.body) }).then(
      () => {
        setDispositionPending(false);
        setDispositionStatus('Saved to CRM ledger');
        void ws.refetchCall(call.id);
      },
      () => {
        setDispositionPending(false);
        setDispositionStatus('Could not save disposition');
      },
    );
  };

  return (
    <ContextCardView
      callId={call.id}
      model={model}
      headingLevel={headingLevel}
      expanded={expanded}
      onToggle={() => setExpanded(e => !e)}
      autoPop={autoPop}
      onAutoPop={on => autoPopPreference.set(on)}
      onPop={popPrimary}
      blocked={blockedVersion === card.version}
      pageHost={typeof window !== 'undefined' ? window.location?.hostname ?? null : null}
      now={now}
      focusRef={focusRef}
      selectedDisposition={selectedDisposition}
      dispositionPending={dispositionPending}
      dispositionStatus={dispositionStatus}
      onDisposition={recordDisposition}
    />
  );
}

export interface ContextCardViewProps {
  callId: string;
  model: ContextCardModel;
  headingLevel?: 3 | 4;
  expanded: boolean;
  onToggle: () => void;
  autoPop: boolean;
  onAutoPop: (on: boolean) => void;
  onPop: () => void;
  blocked: boolean;
  pageHost: string | null;
  now: number;
  focusRef?: RefObject<HTMLHeadingElement | null>;
  selectedDisposition?: string | null;
  dispositionPending?: boolean;
  dispositionStatus?: string | null;
  onDisposition?: (dispositionId: string) => void;
  /** A card from the last call with this number, shown until the call's own card arrives: says so, and offers no
   * disposition buttons or automatic pop-out (those belong to the call's own card). */
  carried?: { note: string; status: string } | null;
}

// React 19 hoists this <style> into <head> once, however many cards render it (the pattern of DropStyles.tsx).
// Tokens only. At the dock's width a label sits above its value; long values wrap and are never cut.
const CSS = `
.ctx-title-text { overflow-wrap: anywhere; min-width: 0; }
.ctx-fields { display: grid; gap: var(--space-2); }
.ctx-field { display: grid; gap: 2px; min-width: 0; }
.ctx-field dt { color: var(--ink-2); font-size: var(--text-meta); line-height: var(--leading-meta); overflow-wrap: anywhere; }
.ctx-field dd { overflow-wrap: anywhere; min-width: 0; }
.btn.ctx-toggle { justify-self: start; }
.ctx-links { display: grid; gap: var(--space-2); }
.ctx-link { display: flex; flex-wrap: wrap; align-items: center; gap: var(--space-1) var(--space-2); min-width: 0; }
.ctx-link a { display: inline-block; min-height: 24px; padding-block: 2px; overflow-wrap: anywhere; min-width: 0; }
.ctx-host { color: var(--ink-2); font-size: var(--text-meta); }
.ctx-section { display: grid; gap: var(--space-1); min-width: 0; padding-top: var(--space-2); border-top: 1px solid var(--line-1); }
.ctx-section-label { color: var(--ink-2); font-size: var(--text-meta); font-weight: 600; line-height: var(--leading-meta); margin: 0; }
.ctx-opener-text { font-size: 1.125rem; line-height: 1.45; font-weight: 500; color: var(--ink-1); margin: 0; overflow-wrap: anywhere; }
.ctx-talking-list { display: grid; gap: var(--space-1); margin: 0; padding-left: var(--space-4); min-width: 0; }
.ctx-talking-list li { font-size: 1.0625rem; line-height: 1.4; font-weight: 500; color: var(--ink-1); overflow-wrap: anywhere; min-width: 0; }
.ctx-carried { display: grid; gap: 2px; margin: 0; padding: var(--space-1) var(--space-2); border-left: 3px solid var(--line-1); color: var(--ink-2); font-size: var(--text-meta); line-height: var(--leading-meta); overflow-wrap: anywhere; min-width: 0; }
.ctx-carried p { margin: 0; }
.ctx-points, .ctx-history-list { display: grid; gap: var(--space-1); margin: 0; padding-left: var(--space-4); min-width: 0; }
.ctx-points li, .ctx-history-item { overflow-wrap: anywhere; min-width: 0; }
.ctx-objection-list { display: grid; gap: var(--space-1); min-width: 0; }
.ctx-objection { border: 1px solid var(--line-1); border-radius: var(--radius-2, 6px); padding: var(--space-1) var(--space-2); min-width: 0; }
.ctx-objection-summary { cursor: pointer; font-weight: 500; overflow-wrap: anywhere; min-width: 0; }
.ctx-objection-response { margin: var(--space-1) 0 0; color: var(--ink-2); overflow-wrap: anywhere; min-width: 0; }
.ctx-next-step-text { margin: 0; overflow-wrap: anywhere; min-width: 0; }
.ctx-dispositions { display: flex; flex-wrap: wrap; gap: var(--space-1); min-width: 0; }
.ctx-disposition-btn { min-height: 36px; overflow-wrap: anywhere; }
.ctx-disposition-btn.is-selected { border-color: var(--ink-1); font-weight: 600; }
.ctx-unknown-line { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: var(--space-2); margin: 0; min-width: 0; }
.ctx-unknown-title { display: inline-flex; align-items: center; gap: var(--space-1); margin: 0; font-size: var(--text-body); }
@media (forced-colors: active) { .capture-band.ctx-card { border: 1px solid CanvasText; } }
`;

export function ContextCardStyles() {
  return <style href="sidetone-context-card-inline" precedence="context-card">{CSS}</style>;
}

/** Presentational card (exported for render tests). */
export function ContextCardView({
  callId, model, headingLevel = 3, expanded, onToggle, autoPop, onAutoPop, onPop, blocked, pageHost, now,
  focusRef, selectedDisposition, dispositionPending = false, dispositionStatus = null, onDisposition, carried = null,
}: ContextCardViewProps) {
  const Heading = headingLevel === 3 ? 'h3' : 'h4';
  const id = `context-${callId}`;
  const updated = formatRelative(model.updatedAt, now);
  const primary = model.links[0] ?? null;
  const carriedNote = carried ? (
    <div className="ctx-carried" data-carried="true">
      <p className="ctx-carried-note">{carried.note}</p>
      <p className="ctx-carried-status" role="status">{carried.status}</p>
    </div>
  ) : null;
  // Pop out automatically belongs to the call's own card; a carried card never offers it.
  const autoPopToggle = primary && !carried ? (
    <label className="check">
      <input type="checkbox" checked={autoPop} onChange={e => onAutoPop(e.currentTarget.checked)} />
      <span>Pop out automatically when a card arrives</span>
    </label>
  ) : null;

  if (model.kind === 'unknown') {
    return (
      <section className="capture-band ctx-card ctx-card-unknown" aria-labelledby={`${id}-title`} data-card-version={model.version}>
        <ContextCardStyles />
        <div className="ctx-unknown-line">
          <Heading id={`${id}-title`} className="capture-title ctx-unknown-title">
            <Icon icon={IdCard} />
            <span className="ctx-title-text"><span className="visually-hidden">Context card: </span>Not in the CRM</span>
          </Heading>
          {primary ? (
            <a href={primary.href} target="_blank" rel="noopener noreferrer">
              {primary.label}<span className="ctx-host"> on {primary.host}</span><span className="visually-hidden"> (opens in a new tab)</span>
            </a>
          ) : null}
        </div>
      </section>
    );
  }

  if (model.kind === 'candidate') {
    const who = model.who;
    const contactSummary = who?.contactName
      ? (who.contactRole ? `${who.contactName}, ${who.contactRole}` : who.contactName)
      : (who?.contactRole ?? null);
    const activeDisposition = selectedDisposition ?? who?.disposition ?? null;
    return (
      <section className="capture-band ctx-card ctx-screen-pop" aria-labelledby={`${id}-title`} data-card-version={model.version}>
        <ContextCardStyles />
        <Heading id={`${id}-title`} ref={focusRef} tabIndex={-1} className="capture-title ctx-pop-heading">
          <Icon icon={IdCard} />
          <span className="ctx-title-text"><span className="visually-hidden">Context card: </span>{who ? who.company : model.title}</span>
        </Heading>
        <p className="meta">
          From {model.source}
          {updated ? <>, updated <time dateTime={model.updatedAt} title={formatAbsolute(model.updatedAt)}>{updated}</time></> : null}
        </p>
        {carriedNote}
        {model.talkingPoints && model.talkingPoints.length ? (
          <div className="ctx-section ctx-talking-points" data-section="talking-points">
            <p className="ctx-section-label">Talking points</p>
            <ul className="ctx-talking-list">
              {model.talkingPoints.map((point, i) => <li key={i}>{point}</li>)}
            </ul>
          </div>
        ) : null}
        <div className="ctx-section ctx-who" data-section="who">
          <p className="ctx-section-label">Who and company</p>
          <dl className="ctx-fields">
            <div className="ctx-field">
              <dt>Company</dt>
              <dd>{who ? who.company : model.title}</dd>
            </div>
            {contactSummary ? (
              <div className="ctx-field">
                <dt>Contact</dt>
                <dd>{contactSummary}</dd>
              </div>
            ) : null}
            {who?.stage ? (
              <div className="ctx-field">
                <dt>Stage</dt>
                <dd>{who.stage}</dd>
              </div>
            ) : null}
            {activeDisposition ? (
              <div className="ctx-field">
                <dt>Disposition</dt>
                <dd>{activeDisposition}</dd>
              </div>
            ) : null}
            {who?.owner ? (
              <div className="ctx-field">
                <dt>Owner</dt>
                <dd>{who.owner}</dd>
              </div>
            ) : null}
          </dl>
        </div>
        {model.opener ? (
          <div className="ctx-section ctx-opener" data-section="opener">
            <p className="ctx-section-label">Opener</p>
            <p className="ctx-opener-text">{model.opener}</p>
          </div>
        ) : null}
        {model.whatToSay && model.whatToSay.length ? (
          <div className="ctx-section ctx-what-to-say" data-section="what-to-say">
            <p className="ctx-section-label">What to say</p>
            <ul className="ctx-points">
              {model.whatToSay.map((point, i) => <li key={i}>{point}</li>)}
            </ul>
          </div>
        ) : null}
        {model.objections && model.objections.length ? (
          <div className="ctx-section ctx-objections" data-section="objections">
            <p className="ctx-section-label">Objections</p>
            <div className="ctx-objection-list">
              {model.objections.map((o, i) => (
                <details key={i} className="ctx-objection">
                  <summary className="ctx-objection-summary">{o.objection}</summary>
                  <p className="ctx-objection-response">{o.response}</p>
                </details>
              ))}
            </div>
          </div>
        ) : null}
        {model.facts && model.facts.length ? (
          <div className="ctx-section ctx-facts" data-section="facts">
            <p className="ctx-section-label">Facts</p>
            <dl className="ctx-fields">
              {model.facts.map((f, i) => (
                <div key={i} className="ctx-field">
                  <dt>{f.label}</dt>
                  <dd>{f.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        ) : null}
        {model.history && model.history.length ? (
          <div className="ctx-section ctx-history" data-section="history">
            <p className="ctx-section-label">History</p>
            <ol className="ctx-history-list">
              {model.history.map((h, i) => {
                const rel = h.at ? formatRelative(h.at, now) : '';
                return (
                  <li key={i} className="ctx-history-item">
                    <span className="ctx-history-summary">{h.summary}</span>
                    {h.at && rel ? <> <time className="meta" dateTime={h.at} title={formatAbsolute(h.at)}>({rel})</time></> : null}
                  </li>
                );
              })}
            </ol>
          </div>
        ) : null}
        <div className="ctx-section ctx-next-step" data-section="next-step">
          <p className="ctx-section-label">Next step</p>
          {model.nextStep ? <p className="ctx-next-step-text">{model.nextStep}</p> : null}
          {carried ? null : <div className="ctx-dispositions" role="group" aria-label="Call disposition">
            {model.dispositions.map(d => {
              const active = activeDisposition === d.id;
              return (
                <button
                  key={d.id}
                  type="button"
                  className={`btn btn-small ctx-disposition-btn${active ? ' is-selected' : ''}`}
                  aria-pressed={active}
                  disabled={dispositionPending}
                  onClick={() => onDisposition?.(d.id)}
                >
                  {d.label}
                </button>
              );
            })}
          </div>}
          {dispositionStatus ? <p className="meta" role="status">{dispositionStatus}</p> : null}
        </div>
        {model.links.length ? (
          <div className="ctx-section ctx-links-section" data-section="links">
            <ul className="ctx-links" aria-label="Links on this card">
              {model.links.map((l, i) => (
                <li key={`${i}-${l.href}`} className="ctx-link">
                  <a href={l.href} target="_blank" rel="noopener noreferrer">
                    {l.label}<span className="ctx-host"> on {l.host}</span><span className="visually-hidden"> (opens in a new tab)</span>
                  </a>
                  {i === 0 ? (
                    <button type="button" className="btn btn-small" onClick={onPop}>
                      <Icon icon={AppWindow} /> Pop out<span className="visually-hidden"> {l.label} in a separate window</span>
                    </button>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {model.refusedLinks ? <p className="meta">{refusedLinksText(model.refusedLinks)}</p> : null}
        {blocked ? <p className="inline-reason" role="status">{blockedText(pageHost)}</p> : null}
        {autoPopToggle}
      </section>
    );
  }

  const fields = shownFields(model, expanded);
  return (
    <section className="capture-band ctx-card" aria-labelledby={`${id}-title`} data-card-version={model.version}>
      <ContextCardStyles />
      <Heading id={`${id}-title`} className="capture-title">
        <Icon icon={IdCard} />
        <span className="ctx-title-text"><span className="visually-hidden">Context card: </span>{model.title}</span>
      </Heading>
      <p className="meta">
        From {model.source}
        {updated ? <>, updated <time dateTime={model.updatedAt} title={formatAbsolute(model.updatedAt)}>{updated}</time></> : null}
      </p>
      {carriedNote}
      {fields.length ? (
        <dl id={`${id}-fields`} className="ctx-fields">
          {fields.map((f, i) => (
            <div key={i} className="ctx-field">
              <dt>{f.label}</dt>
              <dd>{f.value || <span className="meta">Not provided</span>}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {/* A carried card shows every field (expanded) and has no collapse state of its own, so it offers no toggle. */}
      {isCollapsible(model) && !carried ? (
        <button type="button" className="btn btn-small btn-quiet ctx-toggle" aria-expanded={expanded} aria-controls={`${id}-fields`} onClick={onToggle}>
          {toggleText(model, expanded)}
        </button>
      ) : null}
      {model.links.length ? (
        <ul className="ctx-links" aria-label="Links on this card">
          {model.links.map((l, i) => (
            <li key={`${i}-${l.href}`} className="ctx-link">
              <a href={l.href} target="_blank" rel="noopener noreferrer">
                {l.label}<span className="ctx-host"> on {l.host}</span><span className="visually-hidden"> (opens in a new tab)</span>
              </a>
              {i === 0 ? (
                <button type="button" className="btn btn-small" onClick={onPop}>
                  <Icon icon={AppWindow} /> Pop out<span className="visually-hidden"> {l.label} in a separate window</span>
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
      {model.refusedLinks ? <p className="meta">{refusedLinksText(model.refusedLinks)}</p> : null}
      {blocked ? <p className="inline-reason" role="status">{blockedText(pageHost)}</p> : null}
      {autoPopToggle}
    </section>
  );
}
